import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';

const InvoiceGenerator = () => {
    const { branding } = useOutletContext();
    const invoiceRef = useRef(null);

    const [client, setClient] = useState({ name: '', email: '', address: '', phone: '', gstin: '', placeOfSupply: '' });
    const [invoiceInfo, setInvoiceInfo] = useState({
        number: '',
        date: new Date().toLocaleDateString('en-GB'),
        dueDate: '',
        poNumber: '',
        terms: '',
        subject: ''
    });
    const [items, setItems] = useState([{ id: Date.now(), name: '', desc: '', hsn: '', qty: 1, price: 0, cgst: 9, sgst: 9 }]);

    const [isSending, setIsSending] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // ── Invoice history navigation ──
    const [savedInvoices, setSavedInvoices] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(-1); // -1 = new blank invoice

    const fetchInvoices = useCallback(() => {
        axios.get('/api/invoices')
            .then(r => {
                const list = Array.isArray(r.data) ? r.data : [];
                setSavedInvoices(list);
            })
            .catch(() => setSavedInvoices([]));
    }, []);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    const loadInvoice = (inv) => {
        if (!inv) return;
        setInvoiceInfo({
            number:   inv.invoiceNo   || inv.number   || '',
            date:     inv.date        || '',
            dueDate:  inv.dueDate     || '',
            poNumber: inv.poNumber    || inv.po       || '',
            terms:    inv.terms       || '',
            subject:  inv.subject     || '',
        });
        setClient({
            name:          inv.clientName    || inv.client?.name || '',
            email:         inv.clientEmail   || inv.client?.email || '',
            address:       inv.clientAddress || inv.client?.address || '',
            phone:         inv.clientPhone   || inv.client?.phone || '',
            gstin:         inv.clientGstin   || inv.client?.gstin || '',
            placeOfSupply: inv.placeOfSupply || '',
        });
        const rawItems = inv.items || [];
        if (rawItems.length > 0) {
            setItems(rawItems.map((it, i) => ({
                id:    it.id    || Date.now() + i,
                name:  it.name  || it.description || '',
                desc:  it.desc  || it.description || '',
                hsn:   it.hsn   || it.hsnsac      || '',
                qty:   it.qty   || it.quantity     || 1,
                price: it.price || it.rate         || 0,
                cgst:  it.cgst  != null ? it.cgst  : 9,
                sgst:  it.sgst  != null ? it.sgst  : 9,
            })));
        } else {
            setItems([{ id: Date.now(), name: '', desc: '', hsn: '', qty: 1, price: 0, cgst: 9, sgst: 9 }]);
        }
    };

    const goToInvoice = (idx) => {
        if (idx < 0 || idx >= savedInvoices.length) return;
        setCurrentIdx(idx);
        loadInvoice(savedInvoices[idx]);
    };

    const clearForm = () => {
        setCurrentIdx(-1);
        setClient({ name: '', email: '', address: '', phone: '', gstin: '', placeOfSupply: '' });
        setInvoiceInfo({ number: '', date: new Date().toLocaleDateString('en-GB'), dueDate: '', poNumber: '', terms: '', subject: '' });
        setItems([{ id: Date.now(), name: '', desc: '', hsn: '', qty: 1, price: 0, cgst: 9, sgst: 9 }]);
    };

    const saveInvoice = async () => {
        if (!invoiceInfo.number) { alert('Please enter Invoice # before saving.'); return; }
        setIsSaving(true);
        const payload = {
            invoiceNo:     invoiceInfo.number,
            date:          invoiceInfo.date,
            dueDate:       invoiceInfo.dueDate,
            poNumber:      invoiceInfo.poNumber,
            terms:         invoiceInfo.terms,
            subject:       invoiceInfo.subject,
            clientName:    client.name,
            clientEmail:   client.email,
            clientAddress: client.address,
            clientPhone:   client.phone,
            clientGstin:   client.gstin,
            placeOfSupply: client.placeOfSupply,
            items,
            total:         finalTotal,
            status:        'Draft',
        };
        try {
            if (currentIdx !== -1 && savedInvoices[currentIdx]?.id) {
                // Update existing
                await axios.put(`/api/invoices/${savedInvoices[currentIdx].id}`, payload);
                alert(`✅ Invoice ${invoiceInfo.number} updated!`);
            } else {
                // Create new
                await axios.post('/api/invoices', payload);
                alert(`✅ Invoice ${invoiceInfo.number} saved!`);
            }
            await fetchInvoices();
        } catch (e) {
            alert('Save failed — check backend connection.');
        } finally {
            setIsSaving(false);
        }
    };


    const calculateItemTotal = (item) => Number(item.qty) * Number(item.price);
    const calculateItemCGST = (item) => calculateItemTotal(item) * (Number(item.cgst) / 100);
    const calculateItemSGST = (item) => calculateItemTotal(item) * (Number(item.sgst) / 100);

    const subtotal = items.reduce((acc, it) => acc + calculateItemTotal(it), 0);
    const totalCGST = items.reduce((acc, it) => acc + calculateItemCGST(it), 0);
    const totalSGST = items.reduce((acc, it) => acc + calculateItemSGST(it), 0);
    const finalTotal = subtotal + totalCGST + totalSGST;

    const updateItem = (id, field, val) => setItems(items.map(item => item.id === id ? { ...item, [field]: val } : item));
    const addItem = () => setItems([...items, { id: Date.now(), name: '', desc: '', hsn: '998313', qty: 1, price: 0, cgst: 9, sgst: 9 }]);
    const removeItem = (id) => { if (items.length > 1) setItems(items.filter(item => item.id !== id)); };

    const downloadPDF = async () => {
        setIsDownloading(true);
        try {
            const canvas = await html2canvas(invoiceRef.current, { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, (canvas.height * pdfWidth) / canvas.width);
            pdf.save(`Invoice_${invoiceInfo.number.replace('/','_')}.pdf`);
        } catch (e) { alert('PDF export failed!'); }
        finally { setIsDownloading(false); }
    };

    const sendEmail = async () => {
        if (!client.email) { alert('Please enter client email to send!'); return; }
        setIsSending(true);
        try {
            const canvas = await html2canvas(invoiceRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, (canvas.height * pdfWidth) / canvas.width);
            const res = await axios.post('/api/send-invoice', { email: client.email, pdfDataUri: pdf.output('datauristring'), invoiceNo: invoiceInfo.number, clientName: client.name || 'Valued Client' });
            if (res.data.success) { alert('Invoice sent successfully to ' + client.email); }
            else alert('Failed: ' + res.data.error);
        } catch (e) { alert('Email error! Check backend server connection.'); }
        finally { setIsSending(false); }
    };

    return (
        <>
            <style>{`
                .inv-btn { position:relative; overflow:hidden; cursor:pointer; border:none; font-family:'Inter',sans-serif; font-weight:600; border-radius:10px; transition:all 0.25s cubic-bezier(0.4,0,0.2,1); display:flex; align-items:center; gap:8px; justify-content:center; }
                .inv-btn:hover { transform:translateY(-2px); }
                .inv-btn:active { transform:scale(0.97); }
                .item-card { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:16px; margin-bottom:12px; transition:all 0.2s ease; position:relative; }
                .inv-input { width:100%; padding:10px 13px; border-radius:9px; border:1.5px solid #e2e8f0; font-size:14px; font-family:'Inter',sans-serif; color:#1e293b; background:#f8fafc; outline:none; transition:all 0.2s ease; box-sizing:border-box; }
                .inv-input:focus { border-color:#3b82f6; background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,0.12); }
                .section-lbl { font-size:11px; font-weight:800; letter-spacing:1.5px; color:#64748b; text-transform:uppercase; margin:22px 0 12px; display:flex; align-items:center; gap:8px; }
                .nav-arrow { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; color:#475569; font-size:13px; font-weight:700; cursor:pointer; transition:all 0.2s ease; font-family:'Inter',sans-serif; }
                .nav-arrow:hover:not(:disabled) { border-color:#3b82f6; color:#3b82f6; background:#eff6ff; }
                .nav-arrow:disabled { opacity:0.38; cursor:not-allowed; }
            `}</style>

            <div style={{ display:'flex', gap:'28px', fontFamily:'Inter,sans-serif', minHeight:'100vh', backgroundColor:'#f8fafc' }}>
                {/* ── LEFT EDITOR PANEL ── */}
                <div style={{ flex:'0 0 450px', backgroundColor:'#fff', padding:'28px', borderRadius:'16px', boxShadow:'0 4px 24px -8px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9', height:'fit-content' }}>

                    {/* ── Navigation Bar ── */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', background:'#f8fafc', borderRadius:'12px', padding:'10px 14px', border:'1px solid #e2e8f0' }}>
                        <button
                            className="nav-arrow"
                            disabled={currentIdx <= 0}
                            onClick={() => goToInvoice(currentIdx - 1)}
                        >
                            ← Prev
                        </button>

                        <div style={{ textAlign:'center' }}>
                            {savedInvoices.length === 0 ? (
                                <span style={{ fontSize:'12px', color:'#94a3b8', fontWeight:'600' }}>No saved invoices</span>
                            ) : currentIdx === -1 ? (
                                <span style={{ fontSize:'12px', color:'#3b82f6', fontWeight:'700' }}>✨ New Invoice</span>
                            ) : (
                                <div>
                                    <div style={{ fontSize:'12px', fontWeight:'700', color:'#0f172a' }}>
                                        {savedInvoices[currentIdx]?.invoiceNo || savedInvoices[currentIdx]?.number || `Invoice ${currentIdx + 1}`}
                                    </div>
                                    <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'1px' }}>
                                        {currentIdx + 1} of {savedInvoices.length}
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            className="nav-arrow"
                            disabled={currentIdx >= savedInvoices.length - 1}
                            onClick={() => {
                                if (currentIdx === -1 && savedInvoices.length > 0) { goToInvoice(0); }
                                else goToInvoice(currentIdx + 1);
                            }}
                        >
                            Next →
                        </button>
                    </div>

                    {/* New Invoice + Refresh row */}
                    <div style={{ display:'flex', gap:'8px', marginBottom:'4px' }}>
                        <button
                            className="inv-btn"
                            onClick={clearForm}
                            style={{ flex:1, padding:'9px', background: currentIdx === -1 ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : '#f1f5f9', color: currentIdx === -1 ? 'white' : '#475569', fontSize:'13px' }}
                        >
                            ＋ New Invoice
                        </button>
                        <button
                            className="inv-btn"
                            onClick={fetchInvoices}
                            style={{ padding:'9px 14px', background:'#f1f5f9', color:'#475569', fontSize:'13px' }}
                            title="Refresh saved invoices"
                        >
                            🔄
                        </button>
                    </div>

                    <h2 style={{ margin:'18px 0 0', fontSize:'16px', fontWeight:'700', color:'#0f172a' }}>🧾 Edit Tax Invoice</h2>

                    <div className="section-lbl">📋 Invoice Details</div>
                    <div style={{ display:'flex', gap:'10px' }}>
                        <div style={{ flex:1 }}><label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>Invoice #</label><input className="inv-input" value={invoiceInfo.number} onChange={e => setInvoiceInfo({...invoiceInfo, number: e.target.value})} /></div>
                        <div style={{ flex:1 }}><label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>Date</label><input className="inv-input" value={invoiceInfo.date} onChange={e => setInvoiceInfo({...invoiceInfo, date: e.target.value})} /></div>
                    </div>
                    <div style={{ display:'flex', gap:'10px', marginTop:'10px' }}>
                        <div style={{ flex:1 }}><label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>Due Date</label><input className="inv-input" value={invoiceInfo.dueDate} onChange={e => setInvoiceInfo({...invoiceInfo, dueDate: e.target.value})} /></div>
                        <div style={{ flex:1 }}><label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>Terms</label><input className="inv-input" value={invoiceInfo.terms} onChange={e => setInvoiceInfo({...invoiceInfo, terms: e.target.value})} /></div>
                        <div style={{ flex:1 }}><label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>P.O. #</label><input className="inv-input" value={invoiceInfo.poNumber} onChange={e => setInvoiceInfo({...invoiceInfo, poNumber: e.target.value})} /></div>
                    </div>
                    <div style={{ marginTop:'10px' }}>
                        <label style={{ fontSize:'12px', fontWeight:'600', color:'#475569', display:'block', marginBottom:'5px' }}>Subject</label>
                        <input className="inv-input" value={invoiceInfo.subject} onChange={e => setInvoiceInfo({...invoiceInfo, subject: e.target.value})} />
                    </div>

                    <div className="section-lbl">👤 Bill To (Client)</div>
                    <input className="inv-input" placeholder="Client Name" style={{ marginBottom:'10px' }} value={client.name} onChange={e => setClient({...client, name: e.target.value})} />
                    <input className="inv-input" placeholder="GSTIN" style={{ marginBottom:'10px' }} value={client.gstin} onChange={e => setClient({...client, gstin: e.target.value})} />
                    <input className="inv-input" placeholder="Place of Supply" style={{ marginBottom:'10px' }} value={client.placeOfSupply} onChange={e => setClient({...client, placeOfSupply: e.target.value})} />
                    <input className="inv-input" placeholder="Email (for sending PDF)" style={{ marginBottom:'10px' }} value={client.email} onChange={e => setClient({...client, email: e.target.value})} />

                    <div className="section-lbl">🛒 Line Items</div>
                    {items.map(it => (
                        <div key={it.id} className="item-card">
                            <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                                <input className="inv-input" placeholder="Item Name" style={{ flex:2, fontWeight:'700', fontSize:'14px' }} value={it.name} onChange={e => updateItem(it.id, 'name', e.target.value)} />
                                <button className="inv-btn" onClick={() => removeItem(it.id)} style={{ background:'#fef2f2', color:'#ef4444', width:'40px', padding:0, flexShrink:0 }}>✕</button>
                            </div>
                            <textarea className="inv-input" placeholder="Description" style={{ marginBottom:'8px', height:'60px', resize:'none', fontSize:'13px' }} value={it.desc} onChange={e => updateItem(it.id, 'desc', e.target.value)} />
                            <div style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
                                <div style={{ flex:1 }}><label style={{ fontSize:'10px', fontWeight:'700', color:'#94a3b8' }}>HSN/SAC</label><input className="inv-input" value={it.hsn} onChange={e => updateItem(it.id, 'hsn', e.target.value)} style={{ padding:'8px' }} /></div>
                                <div style={{ flex:1 }}><label style={{ fontSize:'10px', fontWeight:'700', color:'#94a3b8' }}>QTY</label><input type="number" className="inv-input" value={it.qty} onChange={e => updateItem(it.id, 'qty', e.target.value)} style={{ padding:'8px' }} /></div>
                                <div style={{ flex:2 }}><label style={{ fontSize:'10px', fontWeight:'700', color:'#94a3b8' }}>RATE (₹)</label><input type="number" className="inv-input" value={it.price} onChange={e => updateItem(it.id, 'price', e.target.value)} style={{ padding:'8px' }} /></div>
                            </div>
                            <div style={{ display:'flex', gap:'8px' }}>
                                <div style={{ flex:1 }}><label style={{ fontSize:'10px', fontWeight:'700', color:'#94a3b8' }}>CGST %</label><input type="number" className="inv-input" value={it.cgst} onChange={e => updateItem(it.id, 'cgst', e.target.value)} style={{ padding:'8px' }} /></div>
                                <div style={{ flex:1 }}><label style={{ fontSize:'10px', fontWeight:'700', color:'#94a3b8' }}>SGST %</label><input type="number" className="inv-input" value={it.sgst} onChange={e => updateItem(it.id, 'sgst', e.target.value)} style={{ padding:'8px' }} /></div>
                            </div>
                        </div>
                    ))}
                    <button className="inv-btn" onClick={addItem} style={{ width:'100%', padding:'13px', border:'2px dashed #cbd5e1', borderRadius:'10px', background:'transparent', color:'#3b82f6', fontSize:'14px' }}>＋ Add Line Item</button>

                    <div style={{ display:'flex', gap:'10px', marginTop:'20px', flexDirection:'column' }}>
                        <button className="inv-btn" onClick={saveInvoice} disabled={isSaving} style={{ width:'100%', padding:'13px', background: isSaving ? '#94a3b8' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color:'white', fontSize:'14px' }}>
                            {isSaving ? '⏳ Saving...' : '💾 Save Invoice'}
                        </button>
                        <div style={{ display:'flex', gap:'10px' }}>
                            <button className="inv-btn" onClick={downloadPDF} disabled={isDownloading} style={{ flex:1, padding:'15px', background: isDownloading ? '#94a3b8' : 'linear-gradient(135deg,#1e3a5f,#0f172a)', color:'white', fontSize:'15px' }}>
                                {isDownloading ? '⏳ Generating...' : '⬇️ Download PDF'}
                            </button>
                            <button className="inv-btn" onClick={sendEmail} disabled={isSending} style={{ flex:1, padding:'15px', background: isSending ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)', color:'white', fontSize:'15px' }}>
                                {isSending ? '📡 Sending...' : '📧 Send via Email'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT: A4 TAX INVOICE PREVIEW ── */}
                <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', overflowX:'auto', paddingBottom:'40px' }}>
                    <div ref={invoiceRef} className="invoice-paper" style={{
                        backgroundColor:'#fff', width:'210mm', minHeight:'297mm', padding:'20mm',
                        boxShadow:'0 10px 60px -15px rgba(0,0,0,0.12)', boxSizing:'border-box', display:'flex', flexDirection:'column',
                        fontFamily:'Arial, sans-serif'
                    }}>
                        {/* Header Section */}
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'40px' }}>
                            <div>
                                <div style={{ fontSize:'28px', fontWeight:'900', color:'#2563eb', letterSpacing:'-1px' }}>
                                    {branding?.logoText || branding?.name || 'Your Company'}
                                </div>
                                {branding?.tagline && <div style={{ fontSize:'11px', fontWeight:'400', color:'#2563eb', marginTop:'-2px' }}>{branding.tagline}</div>}
                            </div>
                            <div style={{ textAlign:'right' }}>
                                <div style={{ fontSize:'24px', fontWeight:'700', color:'#111' }}>TAX INVOICE</div>
                                <div style={{ fontSize:'13px', fontWeight:'600', color:'#111', marginTop:'2px' }}># {invoiceInfo.number}</div>
                                <div style={{ marginTop:'20px', fontSize:'11px', fontWeight:'700', color:'#555' }}>Balance Due</div>
                                <div style={{ fontSize:'18px', fontWeight:'700', color:'#111' }}>₹{finalTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</div>
                            </div>
                        </div>

                        {/* Company Details */}
                        <div style={{ fontSize:'11px', color:'#111', lineHeight:'1.5', marginBottom:'20px' }}>
                            <div style={{ fontWeight:'700' }}>{branding?.name || 'Your Company'}</div>
                            {branding?.address && <div>{branding.address}</div>}
                            {branding?.gstin   && <div>GSTIN {branding.gstin}</div>}
                            {branding?.phone   && <div>{branding.phone}</div>}
                            {branding?.email   && <div>{branding.email}</div>}
                        </div>

                        {/* Client & Invoice Meta */}
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#111', marginBottom:'15px' }}>
                            <div>
                                <div style={{ fontWeight:'700' }}>{client.name}</div>
                                {client.gstin && <div>GSTIN {client.gstin}</div>}
                            </div>
                            <div style={{ width:'200px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}><span style={{color:'#555'}}>Invoice Date :</span><span style={{fontWeight:'600'}}>{invoiceInfo.date}</span></div>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}><span style={{color:'#555'}}>Terms :</span><span style={{fontWeight:'600'}}>{invoiceInfo.terms}</span></div>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}><span style={{color:'#555'}}>Due Date :</span><span style={{fontWeight:'600'}}>{invoiceInfo.dueDate}</span></div>
                                <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{color:'#555'}}>P.O.# :</span><span style={{fontWeight:'600'}}>{invoiceInfo.poNumber}</span></div>
                            </div>
                        </div>

                        {/* Place of Supply & Subject */}
                        <div style={{ fontSize:'11px', color:'#111', marginBottom:'15px' }}>
                            <div style={{ marginBottom:'10px' }}>Place Of Supply: {client.placeOfSupply}</div>
                            <div style={{ fontWeight:'600' }}>Subject :</div>
                            <div>{invoiceInfo.subject}</div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'10px', color:'#111', marginBottom:'10px' }}>
                            <thead>
                                <tr style={{ background:'#333', color:'#fff' }}>
                                    <th style={{ padding:'8px 6px', textAlign:'left', fontWeight:'600', width:'30px' }}>#</th>
                                    <th style={{ padding:'8px 6px', textAlign:'left', fontWeight:'600' }}>Description</th>
                                    <th style={{ padding:'8px 6px', textAlign:'center', fontWeight:'600' }}>HSN/SAC</th>
                                    <th style={{ padding:'8px 6px', textAlign:'center', fontWeight:'600' }}>Qty</th>
                                    <th style={{ padding:'8px 6px', textAlign:'right', fontWeight:'600' }}>Rate</th>
                                    <th style={{ padding:'8px 6px', textAlign:'right', fontWeight:'600' }}>CGST</th>
                                    <th style={{ padding:'8px 6px', textAlign:'right', fontWeight:'600' }}>SGST</th>
                                    <th style={{ padding:'8px 6px', textAlign:'right', fontWeight:'600' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, idx) => (
                                    <tr key={it.id} style={{ borderBottom:'1px solid #eee' }}>
                                        <td style={{ padding:'8px 6px', verticalAlign:'top' }}>{idx+1}</td>
                                        <td style={{ padding:'8px 6px', verticalAlign:'top' }}>
                                            <div style={{ fontWeight:'600' }}>{it.name}</div>
                                            {it.desc && <div style={{ whiteSpace:'pre-wrap', color:'#555', marginTop:'4px', lineHeight:'1.4' }}>{it.desc}</div>}
                                        </td>
                                        <td style={{ padding:'8px 6px', textAlign:'center', verticalAlign:'top' }}>{it.hsn}</td>
                                        <td style={{ padding:'8px 6px', textAlign:'center', verticalAlign:'top' }}>{Number(it.qty).toFixed(2)}</td>
                                        <td style={{ padding:'8px 6px', textAlign:'right', verticalAlign:'top' }}>{Number(it.price).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                                        <td style={{ padding:'8px 6px', textAlign:'right', verticalAlign:'top' }}>
                                            <div>{Number(calculateItemCGST(it)).toLocaleString('en-IN', {minimumFractionDigits:2})}</div>
                                            <div style={{ color:'#666', marginTop:'2px' }}>{it.cgst}%</div>
                                        </td>
                                        <td style={{ padding:'8px 6px', textAlign:'right', verticalAlign:'top' }}>
                                            <div>{Number(calculateItemSGST(it)).toLocaleString('en-IN', {minimumFractionDigits:2})}</div>
                                            <div style={{ color:'#666', marginTop:'2px' }}>{it.sgst}%</div>
                                        </td>
                                        <td style={{ padding:'8px 6px', textAlign:'right', verticalAlign:'top' }}>{Number(calculateItemTotal(it)).toLocaleString('en-IN', {minimumFractionDigits:2})}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals Section */}
                        <div style={{ display:'flex', justifyContent:'flex-end', fontSize:'11px', color:'#111' }}>
                            <div style={{ width:'250px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 6px', borderBottom:'1px solid #eee' }}>
                                    <span style={{ fontWeight:'600' }}>Sub Total</span><span>{subtotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 6px', borderBottom:'1px solid #eee' }}>
                                    <span style={{ fontWeight:'600' }}>CGST9 (9%)</span><span>{totalCGST.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 6px', borderBottom:'1px solid #eee' }}>
                                    <span style={{ fontWeight:'600' }}>SGST9 (9%)</span><span>{totalSGST.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 6px', borderBottom:'1px solid #eee', fontWeight:'700', fontSize:'12px' }}>
                                    <span>Total</span><span>₹{finalTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 6px', fontWeight:'700', fontSize:'12px' }}>
                                    <span>Balance Due</span><span>₹{finalTotal.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default InvoiceGenerator;
